/** @jsxImportSource @compiled/react */
import React, { useCallback, useState } from 'react';
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- React must be in scope for JSX in Jest
void React;

import { defineMessages, useIntl } from 'react-intl';

import Avatar from '@atlaskit/avatar/avatar';
import { cssMap } from '@atlaskit/css';
import Image from '@atlaskit/image/image';
import Heading from '@atlaskit/heading/heading';
import LocationIcon from '@atlaskit/icon/core/location';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { ProfileCardWrapper } from '@confluence/profile/entry-points/ProfileCardWrapper.ts';

import { getHeaderImage } from './headerImages.ts';
import type { ContributorRole, SpaceContributor } from './useSpaceContributors.ts';

const i18n = defineMessages({
	deactivated: {
		id: 'space-contributors.card.deactivated',
		defaultMessage: '({label})',
		description: 'Label shown next to deactivated user names, wrapped in parentheses',
	},
	deactivatedLabel: {
		id: 'space-contributors.card.deactivated.label',
		defaultMessage: 'Deactivated',
		description: 'The word Deactivated shown for inactive users',
	},
	roleAdmin: {
		id: 'space-contributors.card.role.space-admin',
		defaultMessage: 'Space Admin',
		description: 'Role label for a contributor who is an administrator of the space',
	},
	roleOwner: {
		id: 'space-contributors.card.role.space-owner',
		defaultMessage: 'Space Owner',
		description: 'Role label for a contributor who is the owner of the space',
	},
	roleAuthor: {
		id: 'space-contributors.card.role.author',
		defaultMessage: 'Author',
		description:
			'Role label for a contributor who has created a significant number of pages in the space',
	},
	roleReviewer: {
		id: 'space-contributors.card.role.content-reviewer',
		defaultMessage: 'Content Reviewer',
		description:
			'Role label for a contributor who has made a significant number of edits or comments in the space',
	},
});

const roleLabels: Record<ContributorRole, (typeof i18n)[keyof typeof i18n]> = {
	admin: i18n.roleAdmin,
	owner: i18n.roleOwner,
	author: i18n.roleAuthor,
	reviewer: i18n.roleReviewer,
};

// Separator between role labels.
const ROLE_SEPARATOR = ' · ';

export interface ContributorCardProps {
	contributor: SpaceContributor;
}

const styles = cssMap({
	card: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		outlineOffset: token('space.negative.025'),
		cursor: 'pointer',
	},
	cardSelected: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('elevation.surface'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		outlineWidth: token('border.width.selected'),
		outlineStyle: 'solid',
		outlineColor: token('color.border.selected'),
		outlineOffset: token('space.negative.025'),
		cursor: 'pointer',
	},
	cardDeactivated: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		borderRadius: token('radius.xlarge'),
		backgroundColor: token('color.background.neutral'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		outlineOffset: token('space.negative.025'),
		cursor: 'pointer',
	},
	headerImageContainer: {
		// eslint-disable-next-line @atlaskit/design-system/use-tokens-space -- fixed header height
		height: '44px',
		width: '100%',
		overflow: 'hidden',
		borderTopLeftRadius: token('radius.xlarge'),
		borderTopRightRadius: token('radius.xlarge'),
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		flexGrow: 1,
		alignItems: 'flex-start',
		paddingInline: token('space.200'),
		paddingBlockEnd: token('space.200'),
		marginBlockStart: token('space.negative.250'),
		gap: token('space.100'),
	},
	details: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.050'),
	},
	locationPill: {
		display: 'flex',
		alignItems: 'center',
		height: '24px',
		backgroundColor: token('color.background.neutral'),
		borderRadius: token('radius.medium'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		paddingInline: token('space.075'),
		marginBlockStart: 'auto',
	},
});

export const ContributorCard = ({ contributor }: ContributorCardProps): JSX.Element => {
	const intl = useIntl();
	const [isSelected, setIsSelected] = useState(false);

	const handleVisibilityChange = useCallback((isVisible: boolean) => {
		setIsSelected(isVisible);
	}, []);

	return (
		<ProfileCardWrapper
			userId={contributor.accountId}
			trigger="click"
			position="right-start"
			onVisibilityChange={handleVisibilityChange}
		>
			<Box
				// eslint-disable-next-line no-nested-ternary -- simple 3-way style selection
				xcss={
					contributor.isDeactivated
						? styles.cardDeactivated
						: isSelected
							? styles.cardSelected
							: styles.card
				}
				testId={`contributor-card-${contributor.accountId}`}
			>
				<Box xcss={styles.headerImageContainer}>
					<Image src={getHeaderImage(contributor.accountId)} alt="" />
				</Box>
				<Box xcss={styles.content}>
					<Avatar
						name={contributor.displayName}
						src={contributor.avatarUrl}
						size="large"
						borderColor="transparent"
					/>
					<Inline space="space.050" alignBlock="center">
						<Heading size="small">{contributor.displayName}</Heading>
						{contributor.isDeactivated && (
							<Text color="color.text.subtlest" size="small">
								{intl.formatMessage(i18n.deactivated, {
									label: intl.formatMessage(i18n.deactivatedLabel),
								})}
							</Text>
						)}
					</Inline>
					<Box xcss={styles.details}>
						{contributor.jobTitle && (
							<Text color="color.text.subtlest" size="small" weight="bold">
								{contributor.jobTitle}
							</Text>
						)}
						{contributor.roles.length > 0 && (
							<Text color="color.text.subtlest" size="small">
								{contributor.roles
									.map((role) => intl.formatMessage(roleLabels[role]))
									.join(ROLE_SEPARATOR)}
							</Text>
						)}
					</Box>
					{contributor.location && (
						<Box xcss={styles.locationPill}>
							<Inline alignBlock="center" space="space.050">
								<LocationIcon label="" color={token('color.icon.subtlest')} />
								<Text color="color.text.subtlest" size="small">
									{contributor.location}
								</Text>
							</Inline>
						</Box>
					)}
				</Box>
			</Box>
		</ProfileCardWrapper>
	);
};
